import {
  ENDPOINT_CATEGORY_CONFIGS,
  MigrationEndpoint,
  createEndpointKey,
  getCategoryConfig
} from '~/types/migration'
import { EndpointItem } from './EndpointItem'
import { useMemo } from 'react'

interface SelectedEndpointsDisplayProps {
  endpoints: MigrationEndpoint[]
}

export function SelectedEndpointsDisplay({ endpoints }: SelectedEndpointsDisplayProps) {
  // Group endpoints by category for display
  const groupedEndpoints = useMemo(() => {
    const groups: Record<string, MigrationEndpoint[]> = {}
    endpoints.forEach(endpoint => {
      const category = endpoint.category.toUpperCase()
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(endpoint)
    })
    return groups
  }, [endpoints])

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h3
          className='text-[14px] font-bold text-black leading-[18px] mb-2'
          style={{ fontFamily: 'Geist, sans-serif', fontWeight: 700 }}
        >
          Selected Migration Endpoints
        </h3>
        <p
          className='text-[14px] text-[rgba(0,0,0,0.65)]'
          style={{ fontFamily: 'Geist, sans-serif' }}
        >
          The following endpoints have been selected for migration generation:
        </p>
      </div>

      {/* Summary Stats */}
      <div className='flex items-center gap-4 p-4 bg-[#fcfcfc] rounded-lg border border-[#eaeaea]'>
        <div className='flex items-center gap-2'>
          <span
            className='text-[14px] font-semibold text-black'
            style={{ fontFamily: 'Geist, sans-serif' }}
          >
            {endpoints.length}
          </span>
          <span
            className='text-[12px] text-[rgba(0,0,0,0.65)]'
            style={{ fontFamily: 'Geist Mono, monospace', fontWeight: 600 }}
          >
            endpoint{endpoints.length !== 1 ? 's' : ''} selected
          </span>
        </div>

        {/* Category breakdown */}
        {ENDPOINT_CATEGORY_CONFIGS.map(config => {
          const count = endpoints.filter(e => e.category.toUpperCase() === config.category).length

          if (count === 0) return null

          return (
            <div key={config.category} className='flex items-center gap-2'>
              <div
                className={`w-3 h-3 rounded-full ${config.color.replace('text-', 'bg-').replace('100', '200')}`}
              />
              <span
                className='text-[12px] text-[rgba(0,0,0,0.65)]'
                style={{ fontFamily: 'Geist, sans-serif', fontWeight: 500 }}
              >
                {count} {config.label}
              </span>
            </div>
          )
        })}
      </div>

      {/* Endpoints List */}
      <div className='space-y-4'>
        {Object.keys(groupedEndpoints).length === 0 ? (
          <div className='text-center py-8 text-gray-500'>No endpoints selected</div>
        ) : (
          Object.entries(groupedEndpoints).map(([category, categoryEndpoints]) => (
            <div key={category} className='space-y-3'>
              <h4
                className='font-semibold text-gray-600 flex items-center gap-2 uppercase tracking-wide'
                style={{
                  fontFamily: 'Geist Mono',
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: 'normal'
                }}
              >
                {ENDPOINT_CATEGORY_CONFIGS.find(c => c.category === category)?.label || category}
                <span
                  className='text-[12px] text-[rgba(0,0,0,0.65)]'
                  style={{ fontFamily: 'Geist Mono, monospace', fontWeight: 600 }}
                >
                  ({categoryEndpoints.length})
                </span>
              </h4>
              <div className='space-y-2'>
                {categoryEndpoints.map(endpoint => (
                  <div key={createEndpointKey(endpoint)} className='relative'>
                    {/* Read-only endpoint item - no checkbox */}
                    <div className='border border-[#eaeaea] rounded-lg p-4 bg-[#fcfcfc] hover:bg-gray-50 transition-colors'>
                      <div className='flex items-center gap-3'>
                        <div className='w-5 h-5 shrink-0 bg-green-100 border-2 border-green-500 rounded flex items-center justify-center'>
                          <svg
                            className='w-3 h-3 text-green-600'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              fillRule='evenodd'
                              d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </div>
                        <div className='flex items-center gap-2 min-w-0'>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shrink-0 ${getCategoryConfig(endpoint.category).color}`}
                          >
                            {getCategoryConfig(endpoint.category).label}
                          </span>
                          <h4
                            className='text-[14px] font-medium text-black truncate'
                            style={{ fontFamily: 'Geist, sans-serif', fontWeight: 500 }}
                          >
                            {endpoint.name}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
